import type { MouseEventHandler } from "react"
import type { MousePosition } from "../hooks"
import type { Nullable } from "../types/utils"

type ClickableElementProps<T extends HTMLElement> = {
  onClick?: MouseEventHandler<T>
}

export const isClickableElement = <T extends HTMLElement>(
  props: ClickableElementProps<T>,
): boolean => {
  return Boolean(props.onClick)
}

type LinkItemProps = { href: string }

type Props<T extends HTMLElement> = ClickableElementProps<T> | LinkItemProps

export const isLinkElement = <T extends HTMLElement>(
  props: Props<T>,
): props is LinkItemProps => {
  return Boolean((props as LinkItemProps).href)
}

export const isInteractiveElement = <T extends HTMLElement>(
  props: Props<T>,
): boolean => {
  return isLinkElement(props) || isClickableElement(props)
}

export const isMouseWithinElement = <T extends HTMLElement>(
  { x, y }: MousePosition,
  element: Nullable<T>,
): boolean => {
  if (!element) {
    return false
  }

  const rect = element.getBoundingClientRect()

  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

/**
 * Check if the user is currently typing in an input field or textarea.
 * This is useful for preventing keyboard shortcuts from being triggered
 * when the user is actively typing in a form field.
 *
 * @returns true if the current active element is an input or textarea
 */
export const isTypingInInputField = (): boolean => {
  const activeElement = document.activeElement
  if (!activeElement) {
    return false
  }

  const tagName = activeElement.tagName.toUpperCase()
  return tagName === "INPUT" || tagName === "TEXTAREA"
}
